import { Injectable, Injector } from '@angular/core';
import { ApplicationService } from '@app/shared/application/application.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { CreateDeliveryNotesOutput, CreateNdnApplicationDto, CreateOrUpdateSubstancesInput, ExternalSystemEnum, GetNdnOutput, HealthRegisterIdentificationStatus, NdnDto, NdnPersonDto, NdnServiceProxy, NdnStatus, NdnSubstanceDto, PersonApplicationSummaryDto, UpdateNdnPersonInput } from '../../../shared/service-proxies/service-proxies';
import { delay, map, Observable, of, tap } from 'rxjs';

@Injectable({providedIn: 'root'})
export class NdnService extends AppComponentBase {

    ndn: NdnDto;
    ndnCaseId: string;

    constructor(
        injector: Injector,
        private applicationService: ApplicationService,
        private ndnService: NdnServiceProxy
    ) {
        super(injector);
        console.log('NdnService constructor');
    }

    createNdn(input: CreateNdnApplicationDto): Observable<string> {
        this.closeNdn();
        return this.ndnService.createNdn(input).pipe();
    }

    getNdn(caseId: string): Observable<GetNdnOutput> {
        if (!caseId) {
            caseId = this.ndnCaseId || this.ndn?.application?.caseId;
        }
        this.closeNdn();
        return this.ndnService.getNdn(caseId).pipe(delay(1)).pipe(
            tap((output: GetNdnOutput) => {
                this.ndn = output.ndn;
                this.ndnCaseId = output.ndn.application.caseId;
            })
        );
    }

    getMyNdns(): Observable<PersonApplicationSummaryDto[]> {
        return this.ndnService.getMyNdns().pipe();
    }

    closeNdn(): void {
        this.ndn = null;
    }

    getNextStatus(status: NdnStatus): Observable<NdnStatus> {
        let isAuthority = this.permission.isGranted('Pages.Authority.Applications');
        return this.ndnService.getNextStatus(this.caseId, status).pipe(map(status => {
            if (isAuthority) {
                return NdnStatus.FinishReview;
            }
            return status;
        }));
    }

    getPerson(): Observable<NdnPersonDto> {
        return of(this.ndn.ndnPerson).pipe(delay(1));
    }

    getPersonId(): Observable<number> {
        return this.ndnService.getPersonId(this.caseId).pipe();
    }

    getSubstances(): Observable<NdnSubstanceDto[]> {
        return of(this.ndn.ndnSubstances).pipe(delay(1));
    }

    releaseNdn(comment: string): Observable<void> {
        return this.ndnService.releaseNdn(this.caseId, comment);
    }

    getExternalSystemByProfessionId(professionId: string): Observable<ExternalSystemEnum> {
        return this.ndnService.getExternalRegisterByProfessionId(professionId);
    }

    updatePersonWithRegisterCheck(gln: string, professionId: string): Observable<HealthRegisterIdentificationStatus> {
        return this.ndnService.updateNdnPersonWithRegisterCheck(this.caseId, gln, professionId).pipe(
            // tap(() => {
            //     this.getNdn(this.ndn.application.caseId).subscribe();
            // })
        );
    }

    acceptRegisterCheck(input: UpdateNdnPersonInput): Observable<NdnStatus> {
        this.applicationService.setStepToReview(NdnStatus.Person);
        return this.ndnService.acceptRegisterCheck(input).pipe(tap((status: NdnStatus) => {
            this.ndn.currentStep = status;
        }));
    }

    getStepUrl(nextStep: NdnStatus): string {
        return this.applicationService.getNextStep(NdnService.getUrlForStep(nextStep));
    }

    static getUrlForStep(step: NdnStatus): string {
        switch (step) {
            case NdnStatus.FinishReview:
                return 'finish-review';
            case NdnStatus.Person:
                return 'person';
            case NdnStatus.DeliveryNote:
                return 'deliverynote';
            case NdnStatus.Release:
                return 'release';
            default:
                console.error('Invalid status for getUrlForStep');
                return null;
        }
    }

    get caseId(): string {
        return this.ndn?.application?.caseId;
    }

    createOrUpdateSubstances(input: CreateOrUpdateSubstancesInput): Observable<void> {
        this.applicationService.setStepToReview(NdnStatus.DeliveryNote);
        return this.ndnService.createOrUpdateSubstances(input).pipe(
            tap((status: NdnStatus) => {
                this.ndn.currentStep = status;
            })
        ).pipe(map((status: NdnStatus) => null));
    }

    createDeliveryNoteDocument(generateXml: boolean): Observable<CreateDeliveryNotesOutput> {
        return this.ndnService.createDeliveryNotesDocument(this.caseId, generateXml);
    }

    getEvaluationComments(): Observable<string> {
        return this.ndnService.getEvaluationComments(this.caseId);
    }
}
